
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

export default function Pricing() {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState<string | null>(null);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scrollToApply = () => {
    const element = document.getElementById('apply');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const openCheckoutModal = (tierName: string) => {
    setSelectedTier(tierName);
    setIsModalOpen(true);
    setError(null);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTier(null);
    setCustomerName('');
    setCustomerEmail('');
    setError(null);
  };

  const handleCheckout = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTier || !customerName || !customerEmail) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(
        'https://pvdwymehhmhclnpojmue.supabase.co/functions/v1/create-checkout',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            tier: selectedTier,
            customerName,
            customerEmail,
            successUrl: `${window.location.origin}/?payment=success`,
            cancelUrl: `${window.location.origin}/?payment=cancelled`,
          }),
        }
      );

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const tiers = [
    {
      nameKey: 'pricing.elite',
      name: 'Elite Access',
      price: '490',
      featured: false,
      badge: null,
      featureKeys: [
        'pricing.e1',
        'pricing.e2',
        'pricing.e3',
        'pricing.e4',
        'pricing.e5',
        'pricing.e6',
        'pricing.e7',
        'pricing.e8',
      ],
    },
    {
      nameKey: 'pricing.premium',
      name: 'Premium Concierge',
      price: '1,490',
      featured: true,
      badge: null,
      featureKeys: [
        'pricing.p1',
        'pricing.p2',
        'pricing.p3',
        'pricing.p4',
        'pricing.p5',
        'pricing.p6',
        'pricing.p7',
        'pricing.p8',
        'pricing.p9',
      ],
    },
    {
      nameKey: 'pricing.founders',
      name: 'Founders Circle',
      price: '4,900',
      featured: false,
      badge: 'pricing.limited',
      featureKeys: [
        'pricing.f1',
        'pricing.f2',
        'pricing.f3',
        'pricing.f4',
        'pricing.f5',
        'pricing.f6',
        'pricing.f7',
        'pricing.f8',
        'pricing.f9',
        'pricing.f10',
      ],
    },
  ];

  return (
    <section id="pricing" className="py-32 relative">
      <div className="max-w-[1400px] mx-auto px-8">
        <div className="text-center mb-24">
          <p className="text-xs uppercase tracking-[0.3em] text-[#d4af37] mb-5">
            {t('pricing.label')}
          </p>
          <h2 className="text-5xl lg:text-6xl font-light">{t('pricing.title')}</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          {tiers.map((tier, index) => (
            <div
              key={index}
              className={`bg-[#242424] rounded-3xl p-12 border-2 transition-all duration-500 hover:scale-105 hover:shadow-2xl ${
                tier.featured ? 'border-[#d4af37] lg:-mt-10 shadow-xl' : 'border-white/10'
              } ${index === 2 ? 'border-white/20' : ''}`}
              style={{ minHeight: '620px' }}
            >
              <div className="space-y-8">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-2xl font-medium">{t(tier.nameKey)}</h3>
                    {tier.badge && (
                      <span className="text-xs px-3 py-1 bg-[#d4af37]/20 text-[#d4af37] rounded-full whitespace-nowrap">
                        {t(tier.badge)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl text-[#b8b8b8]">&euro;</span>
                    <span className="text-6xl font-bold">{tier.price}</span>
                    {index === 2 && <span className="text-3xl text-[#b8b8b8]">+</span>}
                  </div>
                  <p className="text-sm text-[#888888]">{t('pricing.fee')}</p>
                </div>

                <div className="h-[1px] bg-white/10"></div>

                <div className="space-y-5">
                  {tier.featureKeys.map((fKey, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center mt-0.5">
                        <i className="ri-check-line text-lg text-[#d4af37]"></i>
                      </div>
                      <span className="text-white text-sm leading-relaxed">{t(fKey)}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => index === 2 ? scrollToApply() : openCheckoutModal(tier.name)}
                  className={`w-full py-4 rounded-full text-base font-medium transition-all duration-300 mt-8 cursor-pointer whitespace-nowrap ${
                    index === 2
                      ? 'bg-white text-black hover:bg-[#d4af37] hover:text-black'
                      : 'border-2 border-white/30 text-white hover:bg-white/10'
                  }`}
                >
                  {index === 2 ? t('pricing.apply') : t('pricing.select')}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-16">
          <p className="text-[#888888]">{t('pricing.note')}</p>
        </div>
      </div>

      {/* Checkout Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={closeModal}
          ></div>
          <div className="relative bg-[#1a1a1a] rounded-3xl p-10 max-w-md w-full border border-white/10 shadow-2xl">
            <button
              onClick={closeModal}
              className="absolute top-6 right-6 w-8 h-8 flex items-center justify-center text-white/60 hover:text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>

            <div className="text-center mb-8">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-[#d4af37]/10 rounded-full">
                <i className="ri-vip-crown-line text-3xl text-[#d4af37]"></i>
              </div>
              <h3 className="text-2xl font-medium text-white mb-2">{selectedTier}</h3>
              <p className="text-[#888888] text-sm">Complete your membership purchase</p>
            </div>

            <form onSubmit={handleCheckout} className="space-y-5">
              <div>
                <label className="block text-sm text-white/70 mb-2">Full Name</label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  required
                  className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#d4af37]/50 transition-colors text-sm"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-sm text-white/70 mb-2">Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  required
                  className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#d4af37]/50 transition-colors text-sm"
                  placeholder="Enter your email"
                />
              </div>

              {error && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-4 bg-[#d4af37] text-black rounded-full font-medium hover:bg-[#c4a030] transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="ri-secure-payment-line"></i>
                    Proceed to Payment
                  </>
                )}
              </button>

              <p className="text-center text-xs text-white/40 mt-4">
                <i className="ri-lock-line mr-1"></i>
                Secure payment powered by Stripe
              </p>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
